import { useContext, useState, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  Typography,
  IconButton,
  FormHelperText,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import moment from "moment";
import { BiMinusCircle } from "react-icons/bi";
import { useMutation } from "@apollo/client";
//Import from project
import "../../../../Style/dialogstyle.scss";
import { CREATE_ENROLLMENT_HOUR } from "../../../../Schema/Class";
import { AuthContext } from "../../../../Context/AuthContext";
import { SelectDatePicker } from "../../../Include/SelectDatePicker/SelectDatePicker";

export default function ContinueStudy({
  open,
  editData,
  setRefetch,
  handleClose,
}) {
  // Change Language
  const { setAlert, t } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createEnrollmentHour] = useMutation(CREATE_ENROLLMENT_HOUR, {
    onCompleted: ({ createEnrollmentHour }) => {
      setLoading(false);
      if (createEnrollmentHour?.isSuccess === true) {
        setAlert(true, "success", createEnrollmentHour?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createEnrollmentHour?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageEn: message, messageKh: message });
    },
  });

  const ValidationSchema = Yup.object().shape({
    startHourDate: Yup.string().required(t("required")),
    endHourDate: Yup.string().required(t("required")),
    totalHour: Yup.number().moreThan(0).required(t("required")),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      remark: "",
      totalHour: 0,
      startHourDate: "",
      endHourDate: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      createEnrollmentHour({
        variables: {
          input: {
            remark: values?.remark,
            totalHour: parseFloat(values?.totalHour),
            startHourDate: moment(values?.startHourDate)?.format("YYYY-MM-DD"),
            endHourDate: moment(values?.endHourDate)?.format("YYYY-MM-DD"),
            enrollmentId: editData?._id,
          },
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    resetForm,
    setFieldValue,
    handleSubmit,
    getFieldProps,
  } = formik;

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="xs">
      <DialogTitle sx={{ padding: "10px 20px" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-end"
          justifyContent="center"
        >
          <Typography className="dialog-title">
            {t("modal_title_continue_study_hour")}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleClose}>
            <BiMinusCircle className="close-icon" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_total_hour")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  type="number"
                  size="small"
                  className="text-field"
                  placeholder={t("thead_total_hour")}
                  {...getFieldProps("totalHour")}
                  error={Boolean(touched.totalHour && errors.totalHour)}
                  helperText={touched.totalHour && errors.totalHour}
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        {t("thead_hour")}
                      </Typography>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_hour_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.startHourDate}
                  onChange={(val) => setFieldValue("startHourDate", val || "")}
                />
                {!!errors.startHourDate && touched.startHourDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.startHourDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_end_hour_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.endHourDate}
                  onChange={(val) => setFieldValue("endHourDate", val || "")}
                />
                {!!errors.endHourDate && touched.endHourDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.endHourDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  rows={2}
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("label_remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>
              <Grid item xs={12}>
                {loading ? (
                  <Button className="btn-create" fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className="btn-create"
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
